import { css } from '@emotion/react';

import { breakpoints } from '@coursera/cds-common';

import { getPaletteColor } from './colors';
import type {
  DialogAlignment,
  DialogColor,
  DialogVariant,
  SlotOrientation,
  DialogWidth,
} from './types';

type GetVariantsOptions = {
  isSmall: boolean;
  shouldCenter: boolean;
  isCloseAbsolute: boolean;
  isCloseAbsoluteOnMobile: boolean;
};

const getVariants = ({
  isSmall,
  shouldCenter,
  isCloseAbsolute,
  isCloseAbsoluteOnMobile,
}: GetVariantsOptions) => {
  const spacing = isSmall ? 'var(--cds-spacing-200)' : 'var(--cds-spacing-300)';
  const mobileSpacing = 'var(--cds-spacing-200)';
  const closeButtonWidth = 'var(--cds-spacing-300)';
  const spacingBetweenCloseButtonAndText = 'var(--cds-spacing-100)';

  // For center alignment on standard variant, add extra left padding to account
  // for the close button on the right (only when close is in grid, not absolute)
  const paddingStartWithCentering = `calc(${spacing} + ${closeButtonWidth})`;
  const paddingStart =
    shouldCenter && !isCloseAbsolute ? paddingStartWithCentering : spacing;
  const paddingEnd =
    shouldCenter && !isCloseAbsolute
      ? spacingBetweenCloseButtonAndText
      : spacing;

  // On mobile, only add extra padding if close button is NOT absolute
  const paddingStartWithCenteringMobile = `calc(${spacing} + ${closeButtonWidth})`;
  const paddingStartMobile =
    shouldCenter && !isCloseAbsoluteOnMobile
      ? paddingStartWithCenteringMobile
      : mobileSpacing;

  return {
    standard: css`
      padding-top: ${spacing};
      padding-inline-end: ${paddingEnd};
      padding-inline-start: ${paddingStart};
      padding-bottom: var(--cds-spacing-200);

      ${breakpoints.down('xs')} {
        padding-inline-start: ${paddingStartMobile};
        padding-bottom: var(--cds-spacing-150);
      }
    `,

    // Transactional variant has no close button, so no extra centering padding needed
    transactional: css`
      padding-top: ${spacing};
      padding-inline-start: ${spacing};
      padding-inline-end: ${spacing};
      padding-bottom: var(--cds-spacing-200);

      ${breakpoints.down('xs')} {
        padding-inline-start: var(--cds-spacing-200);
        padding-inline-end: var(--cds-spacing-200);
        padding-bottom: var(--cds-spacing-150);
      }
    `,

    sticky: css`
      position: sticky;
      top: 0;
    `,
  };
};

export const getHeadingGroupCss = ({
  color,
  variant,
  width,
  sticky,
  alignment = 'start',
  hasSlot = false,
  slotOrientation,
  isCloseAbsolute = false,
  isCloseAbsoluteOnMobile = false,
}: {
  color: DialogColor;
  variant: DialogVariant;
  width: DialogWidth;
  sticky: boolean;
  alignment?: DialogAlignment;
  hasSlot?: boolean;
  slotOrientation?: SlotOrientation;
  isCloseAbsolute?: boolean;
  isCloseAbsoluteOnMobile?: boolean;
}) => {
  const isSmall = width === 'small';

  // Apply center alignment if no slot OR if slot is vertical (on top)
  // Center is not allowed for horizontal slots (TypeScript enforces this)
  const shouldCenter =
    alignment === 'center' && (!hasSlot || slotOrientation === 'verticalTop');

  // Determine if sticky should be disabled due to slot being on top
  // When slot is vertical, it's always on top - disable sticky
  const isSlotOnTop = hasSlot && slotOrientation === 'verticalTop';

  // On mobile, horizontal slots cause close button to become absolute
  const isHorizontalSlot =
    slotOrientation === 'horizontalStart' ||
    slotOrientation === 'horizontalEnd';

  const variants = getVariants({
    isSmall,
    shouldCenter,
    isCloseAbsolute,
    isCloseAbsoluteOnMobile,
  });

  // Apply sticky styles, but disable on mobile when horizontal slot moves to top
  const stickyStyles = sticky
    ? css`
        ${variants.sticky}

        ${hasSlot &&
        isHorizontalSlot &&
        css`
          ${breakpoints.down('xs')} {
            position: unset;
          }
        `}
      `
    : null;

  // Border and margin are to fix a sub-pixel rendering issue where a
  // small gap is present between the heading group and the Dialog header
  return css`
    grid-area: headingGroup;
    background-color: ${getPaletteColor(color)};
    ${shouldCenter && 'text-align: center;'}

    ${breakpoints.down('xs')} {
      border-radius: 0;
    }

    ${variants[variant]}

    ${!isSlotOnTop && stickyStyles}
  `;
};
